// src/components/patient/assistant/ChatMessage.jsx
//
// Presentation for AVIA conversation rows: patient and assistant bubbles, the
// working indicator, and a SAFE light formatter for assistant text.
//
// The formatter understands only paragraphs (blank line), "- " / "• " bullets,
// **bold** and "quoted examples", and turns " -- " into an em dash. It builds
// React elements from plain strings -- it never parses or injects HTML, so a
// reply can't carry markup, scripts or links into the page.
//
// The server's "Times are shown in <zone> (<how>)." sentence is lifted out of
// the prose and shown as a small timezone chip under the message.

import React from "react";

const ZONE_SENTENCE = /\s*Times are shown in ([A-Za-z0-9_+\-/]+) \(([^)]*)\)\.\s*/;

export function parseReply(text) {
  let body = String(text || "");
  let zone = null;
  const match = body.match(ZONE_SENTENCE);
  if (match) {
    zone = { name: match[1], how: match[2] };
    body = body.replace(ZONE_SENTENCE, match.index === 0 ? "" : " ");
  }
  body = body.replace(/\s--\s/g, " — ").trim();

  const blocks = [];
  body.split(/\n{2,}/).forEach((chunk) => {
    const lines = chunk.split("\n").map((line) => line.trim()).filter(Boolean);
    let paragraph = [];
    let list = [];
    const flushParagraph = () => {
      if (paragraph.length) blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    };
    const flushList = () => {
      if (list.length) blocks.push({ type: "ul", items: list });
      list = [];
    };
    lines.forEach((line) => {
      const bullet = line.match(/^[-•*]\s+(.*)$/);
      if (bullet) {
        flushParagraph();
        list.push(bullet[1]);
      } else {
        flushList();
        paragraph.push(line);
      }
    });
    flushParagraph();
    flushList();
  });
  return { zone, blocks };
}

// **bold** and "quoted example" spans, as React elements only.
export function renderInline(text) {
  const out = [];
  const pattern = /\*\*([^*]+)\*\*|"([^"\n]{2,160})"/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      out.push(<strong key={key++} className="font-semibold text-slate-900">{match[1]}</strong>);
    } else {
      out.push(
        <span key={key++} className="rounded-md bg-violet-50 px-1.5 py-0.5 font-medium text-violet-800">
          “{match[2]}”
        </span>
      );
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function FormattedReply({ text }) {
  const { zone, blocks } = parseReply(text);
  return (
    <div className="space-y-3 text-[15px] leading-relaxed text-slate-700" data-testid="assistant-reply">
      {blocks.map((block, index) =>
        block.type === "ul" ? (
          <ul key={index} className="space-y-1.5 pl-1">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex} className="flex gap-2">
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" aria-hidden="true" />
                <span className="min-w-0 break-words">{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={index} className="break-words">{renderInline(block.text)}</p>
        )
      )}
      {zone ? (
        <p className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500" data-testid="reply-zone">
          <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span className="truncate">Times in {zone.name} · {zone.how}</span>
        </p>
      ) : null}
    </div>
  );
}

export function AssistantAvatar({ className = "h-9 w-9" }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500 text-sm font-bold text-white shadow-[0_8px_20px_rgba(124,58,237,0.25)] ${className}`}
      aria-hidden="true"
    >
      A
    </span>
  );
}

function shortTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

// One conversation row. Assistant rows: avatar, name/time, bubble, then the
// cards and suggestions aligned under the bubble. Patient rows: right-aligned.
export function MessageRow({ role, createdAt, children, extras }) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end gap-1 sm:max-w-[70%]">
          <p className="sr-only">You said:</p>
          <div className="whitespace-pre-wrap break-words rounded-2xl rounded-tr-md bg-gradient-to-br from-sky-600 to-cyan-600 px-4 py-2.5 text-[15px] leading-relaxed text-white shadow-sm">
            {children}
          </div>
          {createdAt ? <span className="px-1 text-[11px] text-slate-400">You · {shortTime(createdAt)}</span> : null}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <AssistantAvatar />
      <div className="min-w-0 max-w-[92%] flex-1 sm:max-w-[80%]">
        <p className="mb-1 flex items-baseline gap-2 text-xs">
          <span className="font-semibold text-slate-800">AVIA</span>
          {createdAt ? <span className="text-slate-400">{shortTime(createdAt)}</span> : null}
        </p>
        <p className="sr-only">AVIA said:</p>
        <div className="rounded-2xl rounded-tl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">{children}</div>
        {extras ? <div className="mt-3 space-y-3">{extras}</div> : null}
      </div>
    </div>
  );
}

export function WorkingRow({ status }) {
  return (
    <div className="flex items-start gap-3" role="status" data-testid="typing">
      <AssistantAvatar />
      <div className="rounded-2xl rounded-tl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <span className="flex items-center gap-2 text-sm text-slate-600">
          <span className="flex gap-1" aria-hidden="true">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-400 motion-safe:animate-bounce" />
            <span className="h-1.5 w-1.5 rounded-full bg-violet-400 [animation-delay:150ms] motion-safe:animate-bounce" />
            <span className="h-1.5 w-1.5 rounded-full bg-violet-400 [animation-delay:300ms] motion-safe:animate-bounce" />
          </span>
          {status}
        </span>
      </div>
    </div>
  );
}

export default MessageRow;
